import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { Button, Select, SelectItem, TextInput } from '@carbon/react';
import { Download, Filter } from '@carbon/icons-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InlineError } from './states';

// One filter bar for every admin list (P1-D02). Filters live in the page URL, so a reload, the
// back button or a pasted link shows the same list. Text, number and date fields apply on "Lọc" (or Enter,
// the search key of a phone keyboard); a select applies at once; a text field marked `live` also applies
// LIVE_MS after the last key (owner 2026-10-02: the client typed an ID on a phone and nothing filtered). The export button sends the applied filters to the list's own
// endpoint with format=csv (server-side, streamed, audited).

export type FilterField =
  | { key: string; label: string; kind: 'text' | 'number' | 'date'; placeholder?: string; live?: boolean }
  | { key: string; label: string; kind: 'select'; options: readonly (readonly [string, string])[] };

const LIVE_MS = 450;

export type UrlFilters = {
  values: Record<string, string>;
  /** the applied filters without empty values, ready for an API query */
  query: Record<string, string>;
  set: (next: Record<string, string>) => void;
  clear: () => void;
  active: boolean;
  /** the key holds something other than its default */
  changed: (key: string) => boolean;
  page: number;
  pageSize: number;
  setPage: (page: number, pageSize?: number) => void;
};

// `prefix` keeps two lists on one page (a player's ledger and bets tabs) from sharing keys.
export function useUrlFilters(keys: readonly string[], opts: { prefix?: string; defaults?: Record<string, string>; pageSize?: number } = {}): UrlFilters {
  const [params, setParams] = useSearchParams();
  const pre = opts.prefix ?? '';
  const defaults = opts.defaults ?? {};
  // a key present with an empty value means "all" even where the default is a value
  const values = useMemo(() => Object.fromEntries(keys.map((k) => [k, params.has(pre + k) ? params.get(pre + k)! : defaults[k] ?? ''])), [params, keys.join(','), pre]);
  const query = useMemo(() => Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '')), [values]);
  const page = Math.max(1, Number(params.get(pre + 'page')) || 1);
  const pageSize = Math.max(1, Number(params.get(pre + 'size')) || opts.pageSize || 50);
  const write = (next: Record<string, string>, pg?: { page: number; size: number }) => {
    setParams((p) => {
      const out = new URLSearchParams(p);
      for (const k of keys) {
        const v = next[k] ?? '';
        // a value equal to the default is left out, so the plain URL is the default list
        if (v === (defaults[k] ?? '')) out.delete(pre + k);
        else out.set(pre + k, v);
      }
      if (pg && pg.page > 1) out.set(pre + 'page', String(pg.page)); else out.delete(pre + 'page');
      if (pg && pg.size !== (opts.pageSize ?? 50)) out.set(pre + 'size', String(pg.size)); else if (pg) out.delete(pre + 'size');
      return out;
    }); // a new history entry: Back returns to the previous filters or page
  };
  return {
    values, query, page, pageSize,
    active: keys.some((k) => (values[k] ?? '') !== (defaults[k] ?? '')),
    changed: (k) => (values[k] ?? '') !== (defaults[k] ?? ''),
    set: (next) => write(next, { page: 1, size: pageSize }),
    clear: () => write(Object.fromEntries(keys.map((k) => [k, defaults[k] ?? ''])), { page: 1, size: pageSize }),
    setPage: (p, s) => write(values, { page: p, size: s ?? pageSize }),
  };
}

export function ExportButton({ path, query, label = 'Xuất CSV' }: { path: string; query: Record<string, string | number | undefined>; label?: string }) {
  const { can } = useAuth();
  const dl = useMutation({ mutationFn: () => api.download(path, { ...query, format: 'csv' }, 'ruby-export.csv') });
  if (!can('data.export')) return null;
  return (
    <>
      <Button kind="ghost" size="md" renderIcon={Download} disabled={dl.isPending} onClick={() => dl.mutate()}>
        {dl.isPending ? 'Đang xuất...' : label}
      </Button>
      {dl.error ? <div className="toolbar-filters__error"><InlineError error={dl.error} /></div> : null}
    </>
  );
}

export function FilterBar({ id, fields, filters, exportPath, exportQuery, children }: {
  id: string;
  fields: FilterField[];
  filters: UrlFilters;
  /** list endpoint; the export button appears when the admin has data.export */
  exportPath?: string;
  /** extra fixed query for the export (e.g. the player of a tab) */
  exportQuery?: Record<string, string | number | undefined>;
  children?: ReactNode;
}) {
  const [draft, setDraft] = useState(filters.values);
  // phones show the first two fields; the rest open with "Bộ lọc khác" (always open on a wide screen)
  const [more, setMore] = useState(false);
  const hidden = fields.slice(2).filter((f) => filters.changed(f.key)).length;
  const applied = JSON.stringify(filters.values);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mine = useRef<string | null>(null); // what a live apply wrote to the URL: keep typing, do not reset the box
  // the URL changed from outside (back button, a link): show what is applied
  useEffect(() => {
    if (mine.current === applied) { mine.current = null; return; }
    clearTimeout(timer.current);
    setDraft(filters.values);
  }, [applied]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const submit = (e: FormEvent) => { e.preventDefault(); clearTimeout(timer.current); mine.current = null; filters.set(draft); };
  const typed = (f: FilterField, value: string) => {
    const next = { ...draft, [f.key]: f.kind === 'number' ? value.replace(/\D/g, '') : value };
    setDraft(next);
    if (f.kind !== 'text' || !f.live) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { mine.current = JSON.stringify(next); filters.set(next); }, LIVE_MS);
  };
  return (
    <form className={'toolbar-filters' + (more ? '' : ' toolbar-filters--folded')} onSubmit={submit} aria-label="Bộ lọc">
      {fields.map((f, i) => <div key={f.key} className={i >= 2 ? 'toolbar-filters__more' : undefined}>{f.kind === 'select' ? (
        <Select id={`${id}-${f.key}`} labelText={f.label} value={draft[f.key] ?? ''}
          onChange={(e) => { const next = { ...draft, [f.key]: e.target.value }; setDraft(next); filters.set(next); }}>
          {f.options.map(([v, t]) => <SelectItem key={v} value={v} text={t} />)}
        </Select>
      ) : (
        <TextInput id={`${id}-${f.key}`} labelText={f.label} placeholder={f.placeholder}
          type={f.kind === 'date' ? 'date' : 'text'} inputMode={f.kind === 'number' ? 'numeric' : undefined}
          enterKeyHint={f.kind === 'date' ? undefined : 'search'}
          value={draft[f.key] ?? ''}
          onChange={(e) => typed(f, e.target.value)} />
      )}</div>)}
      {fields.length > 2 ? (
        <Button kind="ghost" size="md" className="toolbar-filters__toggle" aria-expanded={more} onClick={() => setMore(!more)}>
          {more ? 'Thu gọn bộ lọc' : `Bộ lọc khác${hidden ? ` (${hidden} đang dùng)` : ''}`}
        </Button>
      ) : null}
      <Button type="submit" kind="tertiary" size="md" renderIcon={Filter}>Lọc</Button>
      {filters.active ? <Button kind="ghost" size="md" onClick={() => filters.clear()}>Bỏ lọc</Button> : null}
      {children}
      <span className="toolbar-filters__grow" />
      {exportPath ? <ExportButton path={exportPath} query={{ ...filters.query, ...exportQuery }} /> : null}
    </form>
  );
}
